import { useEffect, useRef, useState } from "react";
import { HelpCircle } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export default function HelpTooltip({
  content,
  side = "top",
  align = "center",
  mobileLabel = "",
  showLabelOnDesktop = false,
  labelClassName = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = () => {
      setIsOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchend", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchend", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearHoverTimeout();
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150);
  };

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip open={isOpen || isHovered}>
        <span
          className="inline-flex cursor-help"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={mobileLabel ? undefined : content}
              className={cn(
                "cursor-help rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                mobileLabel ? "inline-flex items-center justify-start text-left" : "",
              )}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsOpen((open) => !open);
              }}
            >
              {mobileLabel ? (
                showLabelOnDesktop ? (
                  <span
                    className={cn(
                      "text-[13px] font-medium text-foreground underline decoration-dotted underline-offset-4",
                      labelClassName,
                    )}
                  >
                    {mobileLabel}
                  </span>
                ) : (
                  <>
                    <span
                      className={cn(
                        "md:hidden text-[13px] font-medium text-foreground underline decoration-dotted underline-offset-4",
                        labelClassName,
                      )}
                    >
                      {mobileLabel}
                    </span>
                    <HelpCircle className="hidden h-4 w-4 md:block" />
                  </>
                )
              ) : (
                <HelpCircle className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side={side}
            align={align}
            sideOffset={8}
            className="max-w-64 border border-border/80 bg-background px-3 py-2 text-left text-[13px] leading-relaxed text-foreground shadow-xl shadow-black/10"
            onClick={(event) => event.stopPropagation()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <p>{content}</p>
          </TooltipContent>
        </span>
      </Tooltip>
    </TooltipProvider>
  );
}
